import { motion } from "framer-motion";
import { Scale, Briefcase, Landmark, Check } from "lucide-react";

const AREAS = [
  {
    id: "practice-area-card-personal-injury",
    icon: Scale,
    title: "Personal Injury & Wrongful Death",
    tagline: "Fierce advocacy when catastrophic accidents change your life forever.",
    bullets: ["Car, Truck & Motorcycle Collisions", "Premises Liability & Slip/Falls", "Catastrophic Spine & Brain Injury", "Wrongful Death Litigation"],
    image: "https://images.unsplash.com/photo-1603644448048-28a7e5122f0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxjb3VydGhvdXNlJTIwYXJjaGl0ZWN0dXJlJTIwY29sdW1uc3xlbnwwfHx8fDE3OTM4MDk2MTF8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "practice-area-card-employment-labor",
    icon: Briefcase,
    title: "Employment & Labor Law",
    tagline: "Protecting employees against corporate wage theft & harassment.",
    bullets: ["Unpaid Wages & Overtime Disputes", "Wrongful Termination & Retaliation", "Workplace Discrimination", "Sexual Harassment & Hostile Environment"],
  },
  {
    id: "practice-area-card-civil-rights",
    icon: Landmark,
    title: "Civil Rights & Complex Litigation",
    tagline: "Holding powerful institutions and government entities accountable.",
    bullets: ["Police Misconduct & Excessive Force", "Constitutional Rights Violations", "Multi-Party Commercial Disputes", "Class Action Consumer Rights"],
  },
];

const Card = ({ area, featured, index }) => (
  <motion.article
    data-testid={area.id}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ delay: index * 0.12, duration: 0.7 }}
    className={`group relative overflow-hidden rounded-2xl border border-gold/15 bg-navy-900 transition-all duration-300 hover:border-gold hover:shadow-[0_0_35px_rgba(212,175,55,0.15)] ${featured ? "lg:row-span-2" : ""}`}
  >
    {featured && (
      <div className="relative h-52 overflow-hidden lg:h-64">
        <img src={area.image} alt="Courthouse columns" className="h-full w-full object-cover opacity-50 transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent" />
      </div>
    )}
    <div className="p-7 sm:p-8">
      <area.icon className="mb-5 text-gold" size={30} strokeWidth={1.5} />
      <h3 className="font-display text-xl sm:text-2xl font-semibold text-slate-50">{area.title}</h3>
      <p className="mt-2 text-sm text-slate-400">{area.tagline}</p>
      <ul className="mt-5 space-y-2.5">
        {area.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
            <Check size={15} className="mt-0.5 shrink-0 text-gold" /> {b}
          </li>
        ))}
      </ul>
    </div>
  </motion.article>
);

export const PracticeAreas = () => (
  <section id="practice-areas" className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="mb-12"
    >
      <p className="mb-3 font-mono text-xs tracking-[0.35em] text-gold/80 uppercase">What We Fight For</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-50">
        Practice Areas
      </h2>
    </motion.div>
    <div className="grid gap-6 lg:grid-cols-2">
      <Card area={AREAS[0]} featured index={0} />
      <div className="grid gap-6">
        <Card area={AREAS[1]} index={1} />
        <Card area={AREAS[2]} index={2} />
      </div>
    </div>
  </section>
);
